export function createFukushizaiDirtyStateService(deps) {
    const {
        selectedFukushizaiContextRef,
        selectedQualityContextRef,
        isQualityPanelType,
        fukushizaiDirtyPanelRef,
        fukushizaiBaseDirtyRef,
        fukushizaiDetailSyncingRef,
        entryLoadingRef,
        isEmpty,
        isEnabled,
        DicVal,
        canonicalPanelType,
        normalizeFukushizaiContext,
        isSameFukushizaiItemGroupContext,
        createEmptyFukushizaiContext,
        isFukushizaiBaseField
    } = deps;

    // panel 種別に応じた既定 context を返します。
    //
    // 品質表示は副資材と独立したグループ選択を持ちます。
    // 副資材側の context で dirty を記録すると、両者のグループが違うときに
    // 記録キーと照会キーが一致せず、切替時の確定処理から漏れて編集が失われます。
    function resolveDefaultDirtyContext(panelInfo) {
        const type = canonicalPanelType(DicVal(panelInfo, "type", ""));
        if (typeof isQualityPanelType === "function" && isQualityPanelType(type)) {
            return isEmpty(selectedQualityContextRef) ? selectedFukushizaiContextRef.current : selectedQualityContextRef.current;
        }
        return selectedFukushizaiContextRef.current;
    }

    // item/group/type で dirty panel の一意キーを作ります。
    function buildFukushizaiDirtyKey(panelInfo, context = resolveDefaultDirtyContext(panelInfo)) {
        const type = canonicalPanelType(DicVal(panelInfo, "type", ""));
        const normalized = normalizeFukushizaiContext(context);
        if (type === "" || normalized.itemCd === "" || normalized.groupNo === "") return "";
        return `${normalized.itemCd}|${normalized.groupNo}|${type}`;
    }

    // panel 単位の dirty を立てます。
    function markFukushizaiDirtyPanel(panelInfo, context = resolveDefaultDirtyContext(panelInfo)) {
        const key = buildFukushizaiDirtyKey(panelInfo, context);
        if (key !== "") fukushizaiDirtyPanelRef.current[key] = true;
    }

    // panel 単位の dirty をクリアします。
    function clearFukushizaiDirtyPanel(panelInfo, context = resolveDefaultDirtyContext(panelInfo)) {
        const key = buildFukushizaiDirtyKey(panelInfo, context);
        if (key !== "") delete fukushizaiDirtyPanelRef.current[key];
    }

    // panel 単位の dirty 状態を返します。
    function isFukushizaiDirtyPanel(panelInfo, context = resolveDefaultDirtyContext(panelInfo)) {
        const key = buildFukushizaiDirtyKey(panelInfo, context);
        return key !== "" && isEnabled(DicVal(fukushizaiDirtyPanelRef.current, key, false));
    }

    // 現在 context に対応する BASE dirty fields を返します。
    function readFukushizaiBaseDirtyState(context = null) {
        const dirty = fukushizaiBaseDirtyRef.current || {};
        const dirtyContext = normalizeFukushizaiContext(DicVal(dirty, "context", null));
        const targetContext = context === null ? dirtyContext : normalizeFukushizaiContext(context);
        const fields = DicVal(dirty, "fields", {});
        if (isEmpty(fields) || typeof fields !== "object") return { context: targetContext, fields: {} };
        if (context !== null && !isSameFukushizaiItemGroupContext(dirtyContext, targetContext)) {
            return { context: targetContext, fields: {} };
        }
        return { context: targetContext, fields: { ...fields } };
    }

    // BASE dirty が残っているかを返します。
    function hasFukushizaiBaseDirty(context = null) {
        return Object.keys(readFukushizaiBaseDirtyState(context).fields || {}).length !== 0;
    }

    // BASE 項目 1 件分の dirty を積みます。
    function markFukushizaiBaseDirtyField(fieldId, value, context = selectedFukushizaiContextRef.current) {
        const cid = String(fieldId ?? "").trim();
        if (cid === "" || !isFukushizaiBaseField(cid)) return false;
        if (fukushizaiDetailSyncingRef.current || entryLoadingRef.current) return false;

        const normalized = normalizeFukushizaiContext(context);
        if (normalized.itemCd === "" || normalized.groupNo === "") return false;

        const current = fukushizaiBaseDirtyRef.current || {};
        const currentContext = normalizeFukushizaiContext(DicVal(current, "context", null));
        const nextFields = isSameFukushizaiItemGroupContext(currentContext, normalized)
            ? { ...(DicVal(current, "fields", {}) || {}) }
            : {};
        nextFields[cid] = value;
        fukushizaiBaseDirtyRef.current = { context: normalized, fields: nextFields };
        return true;
    }

    // BASE dirty fields をまとめてクリアします。
    function clearFukushizaiBaseDirtyFields(context = null) {
        if (context === null || hasFukushizaiBaseDirty(context)) {
            fukushizaiBaseDirtyRef.current = { context: createEmptyFukushizaiContext(), fields: {} };
        }
    }

    return {
        buildFukushizaiDirtyKey,
        markFukushizaiDirtyPanel,
        clearFukushizaiDirtyPanel,
        isFukushizaiDirtyPanel,
        readFukushizaiBaseDirtyState,
        hasFukushizaiBaseDirty,
        markFukushizaiBaseDirtyField,
        clearFukushizaiBaseDirtyFields
    };
}
